import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { FileDownload as ExportIcon } from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import { useTheme } from "@mui/material/styles";

import CronBuilderBox from "../../../components/CronBuilderBox";
import { getMutedPanelSx } from "../../../theme/panelStyles";
import { godelApi } from "../../../services/api/omni/godel";

const DEFAULT_CATEGORIES = [
  "neuheiten",
  "parfum",
  "pflege",
  "make-up",
  "premium",
  "herren",
  "x-mas/adventskalender",
].join(", ");

const emptyCredForm = {
  label: "default",
  categories: DEFAULT_CATEGORIES,
  in_stock_qty: 1,
  request_delay_s: 0.25,
  detail_concurrency: 6,
};

const GodelTab = () => {
  const theme = useTheme();

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [credentials, setCredentials] = useState([]);
  const [credForm, setCredForm] = useState(emptyCredForm);
  const [editingId, setEditingId] = useState(null);
  const [credLoading, setCredLoading] = useState(false);

  const [cronValue, setCronValue] = useState("0 5 * * *");
  const [cronStatus, setCronStatus] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalItems, setTotalItems] = useState(0);

  const parseList = (raw) =>
    String(raw || "")
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const fetchCredentials = async () => {
    try {
      const res = await godelApi.listCredentials();
      setCredentials(res.data || []);
      if (res.data?.length && !editingId) {
        const active = res.data.find((c) => c.active) || res.data[0];
        setCredForm({
          label: active.label || "default",
          categories: (active.categories || []).join(", "),
          in_stock_qty: active.in_stock_qty ?? 1,
          request_delay_s: active.request_delay_s ?? 0.25,
          detail_concurrency: active.detail_concurrency ?? 6,
        });
        setEditingId(active.id);
      }
    } catch (err) {
      console.error("Failed to load Godel credentials", err);
    }
  };

  const fetchStatus = async () => {
    try {
      setCronLoading(true);
      const res = await godelApi.getCronStatus();
      setCronStatus(res.data);
      if (res.data?.expression) {
        setCronValue(res.data.expression);
      }
    } catch (err) {
      console.error("Failed to fetch Godel cron status", err);
    } finally {
      setCronLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const res = await godelApi.getProducts(
        page + 1,
        pageSize,
        search,
        sortBy,
        sortOrder,
      );
      setProducts(res.data.items || []);
      setTotalItems(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch Godel products", err);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
    fetchStatus();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [page, pageSize, search, sortBy, sortOrder]);

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const saveCredentials = async () => {
    try {
      setCredLoading(true);
      setError(null);
      setSuccess(null);
      const payload = {
        label: credForm.label.trim() || "default",
        categories: parseList(credForm.categories),
        in_stock_qty: Number(credForm.in_stock_qty) || 1,
        request_delay_s: Number(credForm.request_delay_s) || 0.25,
        detail_concurrency: Number(credForm.detail_concurrency) || 6,
        active: true,
      };
      if (!payload.categories.length) {
        setError("At least one category is required.");
        return;
      }
      if (editingId) {
        await godelApi.updateCredentials(editingId, payload);
        setSuccess("Godel config updated.");
      } else {
        const res = await godelApi.createCredentials(payload);
        setEditingId(res.data.id);
        setSuccess("Godel config saved.");
      }
      await fetchCredentials();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save config.");
    } finally {
      setCredLoading(false);
    }
  };

  const testConnectivity = async () => {
    try {
      setCredLoading(true);
      setError(null);
      setSuccess(null);
      if (editingId) {
        await godelApi.testStoredConnectivity(editingId);
      } else {
        await godelApi.testConnectivity();
      }
      setSuccess("Connectivity OK (using DB proxies).");
    } catch (err) {
      setError(err.response?.data?.detail || "Connectivity test failed.");
    } finally {
      setCredLoading(false);
    }
  };

  const startCron = async () => {
    try {
      setCronLoading(true);
      setError(null);
      setSuccess(null);
      await godelApi.startCron(cronValue);
      setSuccess("Godel catalog cron started/updated.");
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Error starting the cron.");
    } finally {
      setCronLoading(false);
    }
  };

  const stopCron = async () => {
    try {
      setCronLoading(true);
      setError(null);
      setSuccess(null);
      await godelApi.stopCron();
      setSuccess("Godel catalog cron stopped.");
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Error stopping the cron.");
    } finally {
      setCronLoading(false);
    }
  };

  const runScrape = async () => {
    try {
      setScanLoading(true);
      setError(null);
      setSuccess(null);
      const res = await godelApi.scrapeCatalog({});
      const s = res.data || {};
      setSuccess(
        `Scrape done: ${s.products_upserted ?? s.products ?? "?"} products` +
          (s.missing_gtin != null ? ` (${s.missing_gtin} missing GTIN)` : "") +
          (s.categories ? ` across ${s.categories.length} categor(y/ies)` : "") +
          ".",
      );
      await fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || "Scrape failed.");
    } finally {
      setScanLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      setExportLoading(true);
      setError(null);
      const res = await godelApi.exportProductsCsv(search, "name", "asc");
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `godel_products_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess("CSV download started.");
    } catch (err) {
      setError(err.response?.data?.detail || "CSV export failed.");
    } finally {
      setExportLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price == null || price === "") return "-";
    return String(price);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" mb={2}>
          Scrape config
        </Typography>
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
        >
          <TextField
            label="Label"
            value={credForm.label}
            onChange={(e) =>
              setCredForm((p) => ({ ...p, label: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="In-stock qty"
            type="number"
            value={credForm.in_stock_qty}
            onChange={(e) =>
              setCredForm((p) => ({ ...p, in_stock_qty: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Categories (comma-separated paths)"
            value={credForm.categories}
            onChange={(e) =>
              setCredForm((p) => ({ ...p, categories: e.target.value }))
            }
            helperText="e.g. neuheiten, parfum, x-mas/adventskalender"
            fullWidth
            sx={{ gridColumn: { md: "1 / -1" } }}
          />
          <TextField
            label="Request delay (s)"
            type="number"
            inputProps={{ step: 0.05, min: 0 }}
            value={credForm.request_delay_s}
            onChange={(e) =>
              setCredForm((p) => ({ ...p, request_delay_s: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="PDP concurrency"
            type="number"
            inputProps={{ min: 1, max: 32 }}
            value={credForm.detail_concurrency}
            onChange={(e) =>
              setCredForm((p) => ({
                ...p,
                detail_concurrency: e.target.value,
              }))
            }
            helperText="Parallel product-detail fetches for GTIN (proxies from DB)"
            fullWidth
          />
        </Box>
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          <Button
            variant="contained"
            onClick={saveCredentials}
            disabled={credLoading}
          >
            {editingId ? "Update config" : "Save config"}
          </Button>
          <Button
            variant="outlined"
            onClick={testConnectivity}
            disabled={credLoading}
          >
            Test connectivity
          </Button>
          {credentials.length > 0 && (
            <Typography
              variant="body2"
              alignSelf="center"
              color="text.secondary"
            >
              Active: {credentials.find((c) => c.active)?.label || "—"}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" mb={2}>
          Catalog scrape & cron
        </Typography>

        <CronBuilderBox mb={2}>
          <Typography variant="subtitle1" mb={1}>
            Schedule
          </Typography>
          <Cron
            onChange={(e) => setCronValue(e)}
            value={cronValue}
            showResultText={true}
            showResultCron={true}
            locale="en"
          />
        </CronBuilderBox>

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={startCron}
            disabled={cronLoading}
          >
            Start / Update cron
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={stopCron}
            disabled={cronLoading}
          >
            Stop cron
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={runScrape}
            disabled={scanLoading}
          >
            {scanLoading ? "Scraping…" : "Run scrape now"}
          </Button>
          {(cronLoading || scanLoading) && <CircularProgress size={24} />}
        </Box>

        {cronStatus && (
          <Box sx={getMutedPanelSx(theme)}>
            <Typography variant="subtitle2" gutterBottom>
              Status
            </Typography>
            <Typography variant="body2">
              Status:{" "}
              {cronStatus.active ? (
                <span style={{ color: "green" }}>Active</span>
              ) : (
                <span style={{ color: "red" }}>Inactive</span>
              )}
            </Typography>
            <Typography variant="body2">
              Cron: {cronStatus.expression || cronStatus.schedule || "N/A"}
            </Typography>
            {(cronStatus.next_run_at || cronStatus.next_run) && (
              <Typography variant="body2">
                Next run:{" "}
                {cronStatus.active
                  ? new Date(
                      cronStatus.next_run_at || cronStatus.next_run,
                    ).toLocaleString()
                  : "-"}
              </Typography>
            )}
            {cronStatus.last_run_at && (
              <Typography variant="body2">
                Last run: {new Date(cronStatus.last_run_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6">Products ({totalItems})</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              label="Search name / SKU / GTIN"
              variant="outlined"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
            <Button
              onClick={fetchProducts}
              disabled={productsLoading}
              size="small"
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              onClick={runScrape}
              disabled={scanLoading}
              size="small"
              variant="contained"
              color="secondary"
            >
              {scanLoading ? "Importing…" : "Import Products"}
            </Button>
            <Button
              onClick={exportCsv}
              disabled={exportLoading}
              size="small"
              variant="contained"
              startIcon={
                exportLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ExportIcon />
                )
              }
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "name"}
                    direction={sortBy === "name" ? sortOrder : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>URL</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "sku"}
                    direction={sortBy === "sku" ? sortOrder : "asc"}
                    onClick={() => handleSort("sku")}
                  >
                    SKU
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "gtin13"}
                    direction={sortBy === "gtin13" ? sortOrder : "asc"}
                    onClick={() => handleSort("gtin13")}
                  >
                    GTIN
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "price"}
                    direction={sortBy === "price" ? sortOrder : "asc"}
                    onClick={() => handleSort("price")}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>
                <TableCell>Currency</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No products yet. Save config and run a scrape.
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>
                      {p.url ? (
                        <Link
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          Open
                        </Link>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{p.sku || "-"}</TableCell>
                    <TableCell>{p.gtin13 || "-"}</TableCell>
                    <TableCell>{formatPrice(p.price)}</TableCell>
                    <TableCell>{p.currency || "EUR"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default GodelTab;
